import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Shield, Building2, Activity, ArrowLeft, AlertCircle, Mail, BarChart3, DollarSign, Zap, TrendingUp, Users, Settings, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AccountManagementPanel } from "@/components/platform-admin/AccountManagementPanel";
import { ActivityMonitor } from "@/components/platform-admin/ActivityMonitor";
import { EmailDiagnostics } from "@/components/platform-admin/EmailDiagnostics";
import { PlatformAnalyticsDashboard } from "@/components/platform-admin/PlatformAnalyticsDashboard";
import { SubscriptionAnalytics } from "@/components/platform-admin/SubscriptionAnalytics";
import { UsageMetrics } from "@/components/platform-admin/UsageMetrics";
import { SystemHealthPanel } from "@/components/platform-admin/SystemHealthPanel";
import { ConversionFunnel } from "@/components/platform-admin/ConversionFunnel";
import { OrgOnboardingPanel } from "@/components/platform-admin/OrgOnboardingPanel";
import { UserManagementPanel } from "@/components/platform-admin/UserManagementPanel";
import { toast } from "sonner";
export default function PlatformAdmin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isPlatformOwner, setIsPlatformOwner] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        toast.error("Please log in to access this page");
        navigate('/auth');
      } else {
        checkPlatformOwnerAccess();
      }
    }
  }, [authLoading, user]);

  const checkPlatformOwnerAccess = async () => {
    try {
      if (!user) return;

      // Check if user has platform owner role (owner role with NULL org_id)
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role, org_id')
        .eq('user_id', user.id)
        .eq('role', 'owner')
        .is('org_id', null);

      if (error) throw error;

      const hasAccess = roles && roles.length > 0;
      setIsPlatformOwner(hasAccess);

      if (!hasAccess) {
        toast.error("Access Denied: Platform Owner privileges required");
        navigate('/');
      }
    } catch (error: any) {
      console.error('Access check error:', error);
      toast.error("Failed to verify access");
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-primary animate-pulse" />
          <p className="text-muted-foreground">Verifying platform owner access...</p>
        </div>
      </div>
    );
  }

  if (!isPlatformOwner) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="border-b pb-4 space-y-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="mr-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Shield className="w-8 h-8 text-primary" />
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">Platform Administration</h1>
                <Badge variant="default" className="bg-primary">
                  Platform Owner
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Logged in as: {user?.email}
              </p>
            </div>
          </div>

          {/* Context Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Platform Owner Access:</strong> You can manage all organizations and system settings. 
              However, you won't see org-specific data (jobs, emails, leads) unless you're also added as a member to an organization.
            </AlertDescription>
          </Alert>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="onboarding" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Onboarding
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="accounts" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Organizations
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Usage
            </TabsTrigger>
            <TabsTrigger value="health" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Health
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            <PlatformAnalyticsDashboard />
            <div className="grid lg:grid-cols-2 gap-6">
              <ConversionFunnel />
            </div>
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-6">
            <SubscriptionAnalytics />
          </TabsContent>

          <TabsContent value="onboarding" className="mt-6">
            <OrgOnboardingPanel />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <UserManagementPanel />
          </TabsContent>

          <TabsContent value="accounts" className="mt-6">
            <AccountManagementPanel />
          </TabsContent>

          <TabsContent value="usage" className="mt-6">
            <UsageMetrics />
          </TabsContent>

          <TabsContent value="health" className="mt-6">
            <SystemHealthPanel />
          </TabsContent>

          <TabsContent value="activity" className="mt-6">
            <ActivityMonitor />
          </TabsContent>

          <TabsContent value="email" className="mt-6">
            <EmailDiagnostics />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
